import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent } from "@/shared/components/ui/sheet";
import {
  Search, Webhook, ArrowDownLeft, ArrowUpRight,
  RefreshCw, Code2, Activity, AlertTriangle,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusCfg = {
  received:          { color: "#38bdf8", bg: "rgba(56,189,248,0.09)",  border: "rgba(56,189,248,0.22)",  label: "Received",    glow: "rgba(56,189,248,0.22)" },
  processed:         { color: "#10b981", bg: "rgba(16,185,129,0.09)",  border: "rgba(16,185,129,0.22)",  label: "Processed",   glow: "rgba(16,185,129,0.22)" },
  failed:            { color: "#ef4444", bg: "rgba(239,68,68,0.09)",   border: "rgba(239,68,68,0.22)",   label: "Failed",      glow: "rgba(239,68,68,0.22)"  },
  invalid_signature: { color: "#f97316", bg: "rgba(249,115,22,0.09)",  border: "rgba(249,115,22,0.22)",  label: "Invalid Sig", glow: "rgba(249,115,22,0.22)" },
};

const dirCfg = {
  inbound:  { icon: ArrowDownLeft, color: "#38bdf8", bg: "rgba(56,189,248,0.1)", border: "rgba(56,189,248,0.22)" },
  outbound: { icon: ArrowUpRight,  color: "#10b981", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.22)" },
};

/* ── Stat Card — same style as dashboard ── */
function StatCard({ label, value, color, icon: Icon, delay = 0 }) {
  const [hov, setHov] = useState(false);
  const [gp, setGp] = useState({ x: 50, y: 50 });
  const ref = React.useRef(null);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 20 }}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onMouseMove={(e) => { const r = ref.current?.getBoundingClientRect(); if (r) setGp({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
      whileHover={{ y: -5, scale: 1.025, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      style={{
        position: "relative", overflow: "hidden", borderRadius: "18px", padding: "20px",
        cursor: "default", fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: "linear-gradient(150deg, rgba(8,16,34,0.98) 0%, rgba(11,22,44,0.96) 100%)",
        border: hov ? `1px solid ${color}42` : "1px solid rgba(255,255,255,0.054)",
        boxShadow: hov ? `0 16px 48px -8px ${color}28, 0 0 0 1px ${color}16` : "0 3px 18px rgba(0,0,0,0.38)",
        transition: "border 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      {/* Cursor glow */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(circle at ${gp.x}% ${gp.y}%, ${color}16 0%, transparent 62%)`, opacity: hov ? 1 : 0, transition: "opacity 0.3s ease" }} />
      <div style={{ position: "absolute", top: 0, right: 0, width: "70px", height: "70px", background: `radial-gradient(circle at 100% 0%, ${color}10 0%, transparent 70%)`, transform: "translate(20%,-20%)", pointerEvents: "none" }} />

      {/* Bottom line */}
      <motion.div
        style={{ position: "absolute", bottom: 0, left: 0, height: "2px", background: `linear-gradient(90deg, ${color}cc, ${color}55, transparent)` }}
        animate={{ width: hov ? "100%" : "0%", opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
      />

      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: `${color}90`, marginBottom: "10px" }}>{label}</p>
          <motion.p
            style={{ fontSize: "26px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.04em", margin: 0 }}
            animate={{ scale: hov ? 1.05 : 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >{value}</motion.p>
        </div>

        <motion.div
          animate={{ rotate: hov ? [0, -8, 5, -2, 0] : 0, scale: hov ? 1.15 : 1, y: hov ? -2 : 0 }}
          transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div style={{
            width: "44px", height: "44px", borderRadius: "13px",
            background: `${color}14`, border: `1px solid ${color}30`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: hov ? `0 8px 22px ${color}22` : "none", transition: "box-shadow 0.3s ease",
          }}>
            <Icon style={{ width: "18px", height: "18px", color }} />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function ResellerWebhooks() {
  const [search, setSearch] = useState("");
  const [dirFilter, setDirFilter] = useState("all");
  const [selectedLog, setLog] = useState(null);
  const [sfocus, setSfocus] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { t } = useLanguage();

  const { data: logs = [], isLoading, refetch } = useQuery({ queryKey: ["webhook-logs"], queryFn: async () => [] });

  const filtered = logs.filter(log => {
    const ms = !search || log.webhook_id?.toLowerCase().includes(search.toLowerCase()) || log.transaction_id?.toLowerCase().includes(search.toLowerCase());
    return ms && (dirFilter === "all" || log.direction === dirFilter);
  });

  const inCount   = logs.filter(l => l.direction === "inbound").length;
  const outCount  = logs.filter(l => l.direction === "outbound").length;
  const failCount = logs.filter(l => ["failed", "invalid_signature"].includes(l.status)).length;

  const statCards = [
    { label: "Total",    value: logs.length, color: "#06b6d4", icon: Activity,      delay: 0    },
    { label: "Inbound",  value: inCount,      color: "#38bdf8", icon: ArrowDownLeft, delay: 0.07 },
    { label: "Outbound", value: outCount,     color: "#10b981", icon: ArrowUpRight,  delay: 0.14 },
    { label: "Failed",   value: failCount,    color: "#ef4444", icon: AlertTriangle, delay: 0.21 },
  ];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
    .wh-page { font-family: 'Plus Jakarta Sans', sans-serif; max-width: 1400px; }
    .wh-stat-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
    @media(min-width:640px)  { .wh-stat-grid { grid-template-columns: repeat(4,1fr); } }
    @keyframes wh-spin { to { transform: rotate(360deg); } }
    @keyframes wh-pulse { 0%,100%{ box-shadow:0 0 0 0 rgba(52,211,153,0.5); } 50%{ box-shadow:0 0 0 5px rgba(52,211,153,0); } }

    .dir-pill {
      padding: 8px 15px; border-radius: 20px; font-size: 12px; font-weight: 700;
      cursor: pointer; transition: all 0.32s cubic-bezier(0.34,1.56,0.64,1);
      border: 1px solid transparent; font-family: 'Plus Jakarta Sans', sans-serif; outline: none;
    }
    .dir-pill.active {
      background: linear-gradient(135deg, rgba(6,182,212,0.18), rgba(99,102,241,0.14));
      border-color: rgba(6,182,212,0.38); color: #06b6d4;
      box-shadow: 0 4px 16px rgba(6,182,212,0.14); transform: scale(1.05);
    }
    .dir-pill:not(.active) { background: rgba(8,16,34,0.8); border-color: rgba(255,255,255,0.06); color: #334155; }
    .dir-pill:not(.active):hover { background: rgba(255,255,255,0.04); color: #64748b; border-color: rgba(255,255,255,0.09); transform: scale(1.02); }

    .wh-table-wrap {
      background: linear-gradient(150deg, rgba(8,16,34,0.98) 0%, rgba(11,22,44,0.96) 100%);
      border: 1px solid rgba(255,255,255,0.052); border-radius: 20px; overflow: hidden;
      box-shadow: 0 6px 32px rgba(0,0,0,0.38);
    }
    .wh-row { border-bottom: 1px solid rgba(255,255,255,0.034); cursor: pointer; transition: background 0.18s ease; }
    .wh-row:hover { background: rgba(6,182,212,0.04) !important; }
    .wh-row:last-child { border-bottom: none; }

    .detail-sheet {
      background: linear-gradient(160deg, #060d1a 0%, #08111e 100%) !important;
      border-left: 1px solid rgba(6,182,212,0.12) !important;
    }
    .code-pre {
      background: rgba(4,8,18,0.95); padding: 13px 15px; border-radius: 12px;
      font-family: 'JetBrains Mono', monospace; font-size: 11px;
      overflow-x: auto; max-height: 200px; overflow-y: auto;
      border: 1px solid rgba(6,182,212,0.11);
      scrollbar-width: thin; scrollbar-color: rgba(6,182,212,0.18) transparent;
    }
    .code-pre.green { color: #6ee7b7; border-color: rgba(16,185,129,0.12); }
    .code-pre { color: #7dd3fc; }
    .code-pre::-webkit-scrollbar { width: 3px; height: 3px; }
    .code-pre::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.18); border-radius: 3px; }
  `;

  return (
    <ResellerLayout currentPage="ResellerWebhooks" resellerName="Asia Gaming Network">
      <style>{css}</style>
      <div className="wh-page">

        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: "24px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
              style={{ width: "46px", height: "46px", borderRadius: "14px", background: "linear-gradient(135deg, #0ea5e9, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 22px rgba(14,165,233,0.45), inset 0 1px 0 rgba(255,255,255,0.18)" }}>
              <Webhook style={{ width: "20px", height: "20px", color: "#fff" }} />
            </motion.div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "9px", marginBottom: "2px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.04em", margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t("webhookLogs") || "Webhook Logs"}
                </h1>
                <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#34d399", animation: "wh-pulse 2s ease-in-out infinite" }} />
              </div>
              <p style={{ fontSize: "12px", color: "#334155", margin: 0, fontWeight: 500 }}>
                {filtered.length} {t("logs") || "logs"} · Real-time
              </p>
            </div>
          </div>

          <motion.button onClick={async () => { setRefreshing(true); await refetch(); setTimeout(() => setRefreshing(false), 650); }}
            whileHover={{ scale: 1.06, boxShadow: "0 8px 24px rgba(6,182,212,0.2)" }} whileTap={{ scale: 0.94 }}
            style={{ display: "flex", alignItems: "center", gap: "7px", padding: "10px 18px", borderRadius: "13px", background: "rgba(6,182,212,0.07)", border: "1px solid rgba(6,182,212,0.22)", color: "#06b6d4", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none" }}>
            <motion.div animate={{ rotate: refreshing ? 360 : 0 }} transition={{ duration: 0.7, ease: "linear", repeat: refreshing ? Infinity : 0 }}>
              <RefreshCw style={{ width: "13px", height: "13px" }} />
            </motion.div>
            Refresh
          </motion.button>
        </motion.div>

        {/* STAT CARDS */}
        <div className="wh-stat-grid" style={{ marginBottom: "22px" }}>
          {statCards.map(c => <StatCard key={c.label} {...c} />)}
        </div>

        {/* FILTERS */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}
          style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "14px", alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 200px", maxWidth: "360px" }}>
            <Search style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", width: "14px", height: "14px", color: sfocus ? "#06b6d4" : "#334155", transition: "color 0.22s ease" }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onFocus={() => setSfocus(true)} onBlur={() => setSfocus(false)}
              placeholder="Search webhook ID, tx ID…"
              style={{
                width: "100%", padding: "10px 14px 10px 38px", borderRadius: "13px",
                background: sfocus ? "rgba(6,182,212,0.05)" : "rgba(8,16,34,0.9)",
                border: sfocus ? "1px solid rgba(6,182,212,0.4)" : "1px solid rgba(255,255,255,0.07)",
                color: "#c8dff0", fontSize: "13px", outline: "none", transition: "all 0.25s ease",
                boxShadow: sfocus ? "0 0 0 3px rgba(6,182,212,0.08)" : "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            {["all", "inbound", "outbound"].map(d => (
              <motion.button key={d} className={`dir-pill ${dirFilter === d ? "active" : ""}`}
                onClick={() => setDirFilter(d)} whileTap={{ scale: 0.95 }}>
                {d === "all" ? "All" : d.charAt(0).toUpperCase() + d.slice(1)}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* TABLE */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }} className="wh-table-wrap">
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(4,8,18,0.6)" }}>
                  {["Webhook", "Event", "Status", "Response", "Date"].map((h, i) => (
                    <th key={i} style={{ padding: "13px 18px", textAlign: "left", fontSize: "9px", fontWeight: 800, color: "#1e2d45", textTransform: "uppercase", letterSpacing: "0.14em", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} style={{ textAlign: "center", padding: "72px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.85, ease: "linear", repeat: Infinity }}>
                        <Webhook style={{ width: "26px", height: "26px", color: "#06b6d4" }} />
                      </motion.div>
                      <span style={{ fontSize: "12px", color: "#334155", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("loading") || "Loading…"}</span>
                    </div>
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: "center", padding: "72px 24px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                      <motion.div animate={{ y: [0, -7, 0], opacity: [0.4, 0.8, 0.4] }} transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                        style={{ width: "52px", height: "52px", borderRadius: "16px", background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Webhook style={{ width: "22px", height: "22px", color: "#1e3a52" }} />
                      </motion.div>
                      <div>
                        <p style={{ margin: "0 0 3px", fontSize: "14px", fontWeight: 700, color: "#1e2d45", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("noLogs") || "No webhook logs found"}</p>
                        <p style={{ margin: 0, fontSize: "12px", color: "#1a2534" }}>Logs will appear when webhooks are triggered</p>
                      </div>
                    </div>
                  </td></tr>
                ) : filtered.map((log, i) => {
                  const sc = statusCfg[log.status] || statusCfg.received;
                  const dc = dirCfg[log.direction] || dirCfg.inbound;
                  const DirIcon = dc.icon;
                  return (
                    <motion.tr key={log.id || i} className="wh-row"
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.022, type: "spring", stiffness: 180, damping: 22 }}
                      onClick={() => setLog(log)}
                    >
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
                          <motion.div whileHover={{ rotate: 8, scale: 1.12 }} transition={{ type: "spring", stiffness: 350 }}
                            style={{ width: "36px", height: "36px", borderRadius: "11px", background: dc.bg, border: `1px solid ${dc.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DirIcon style={{ width: "14px", height: "14px", color: dc.color }} />
                          </motion.div>
                          <div>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "#06b6d4", fontWeight: 600 }}>{log.webhook_id?.slice(0, 12)}…</span>
                            {log.source && <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#334155" }}>{log.source}</p>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "14px 18px" }}>
                        <span style={{ fontSize: "11px", color: "#475569", background: "rgba(100,116,139,0.09)", padding: "3px 9px", borderRadius: "7px", border: "1px solid rgba(100,116,139,0.12)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          {log.event_type?.replace(/\./g, " ") || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 18px" }}>
                        <motion.span whileHover={{ scale: 1.04 }}
                          style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "4px 10px", borderRadius: "20px", background: sc.bg, border: `1px solid ${sc.border}`, fontSize: "11px", fontWeight: 700, color: sc.color, boxShadow: `0 2px 10px ${sc.glow}` }}>
                          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: sc.color }} />
                          {sc.label}
                        </motion.span>
                      </td>
                      <td style={{ padding: "14px 18px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: (log.response_status && log.response_status < 400) ? "#10b981" : "#ef4444" }}>
                          {log.response_status || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 18px" }}>
                        <span style={{ fontSize: "12px", color: "#475569", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          {log.created_date ? format(new Date(log.created_date), "MMM d, HH:mm") : "N/A"}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* DETAIL SHEET */}
      <Sheet open={!!selectedLog} onOpenChange={() => setLog(null)}>
        <SheetContent className="detail-sheet w-full sm:max-w-lg overflow-y-auto" style={{ padding: 0 }}>
          <AnimatePresence>
            {selectedLog && (
              <motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 18 }} style={{ padding: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "13px", marginBottom: "22px", paddingBottom: "18px", borderBottom: "1px solid rgba(6,182,212,0.08)" }}>
                  <div style={{ width: "42px", height: "42px", borderRadius: "13px", background: "linear-gradient(135deg, rgba(6,182,212,0.16), rgba(99,102,241,0.12))", border: "1px solid rgba(6,182,212,0.26)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(6,182,212,0.12)" }}>
                    <Webhook style={{ width: "18px", height: "18px", color: "#06b6d4" }} />
                  </div>
                  <div>
                    <p style={{ margin: "0 0 5px", fontSize: "15px", fontWeight: 800, color: "#f0f9ff", fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "-0.02em" }}>Webhook Log</p>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 10px", borderRadius: "20px", background: (statusCfg[selectedLog.status] || statusCfg.received).bg, border: `1px solid ${(statusCfg[selectedLog.status] || statusCfg.received).border}`, fontSize: "10px", fontWeight: 700, color: (statusCfg[selectedLog.status] || statusCfg.received).color }}>
                      {(statusCfg[selectedLog.status] || statusCfg.received).label}
                    </span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "18px" }}>
                  {[{ label: "Direction", value: selectedLog.direction }, { label: "Event", value: selectedLog.event_type }, { label: "Source", value: selectedLog.source }, { label: "HTTP", value: selectedLog.response_status || "—" }].map(({ label, value }, idx) => (
                    <motion.div key={label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * idx }}
                      whileHover={{ scale: 1.02, borderColor: "rgba(6,182,212,0.2)" }}
                      style={{ background: "rgba(8,16,34,0.8)", borderRadius: "12px", padding: "12px 13px", border: "1px solid rgba(255,255,255,0.055)", transition: "border 0.22s ease" }}>
                      <p style={{ margin: "0 0 4px", fontSize: "9px", color: "#1e2d45", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</p>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#c8dff0", wordBreak: "break-all", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value || "—"}</p>
                    </motion.div>
                  ))}
                </div>

                {selectedLog.payload && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={{ marginBottom: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "7px" }}>
                      <Code2 style={{ width: "12px", height: "12px", color: "#06b6d4" }} />
                      <p style={{ margin: 0, fontSize: "9px", color: "#334155", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Payload</p>
                    </div>
                    <pre className="code-pre">{selectedLog.payload}</pre>
                  </motion.div>
                )}

                {selectedLog.response_body && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "7px" }}>
                      <Activity style={{ width: "12px", height: "12px", color: "#10b981" }} />
                      <p style={{ margin: 0, fontSize: "9px", color: "#334155", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Response</p>
                    </div>
                    <pre className="code-pre green">{selectedLog.response_body}</pre>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </SheetContent>
      </Sheet>
    </ResellerLayout>
  );
}